/**
 * MarkerMapPage (iter1065) — карта на показателите.
 * За всеки показател: моята стойност, ориентировъчните граници, близнакът,
 * който го ОЦЕНЯВА, и близнаците, които го ПОЛЗВАТ.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { ArrowLeft, Loader2, Search, ArrowRight } from "lucide-react";

export default function MarkerMapPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("всички");

  useEffect(() => {
    axios.get(`${API}/marker-map`).then(({ data }) => setData(data)).catch(() => setData({ markers: [] }));
  }, [API]);

  const shown = useMemo(() => {
    const rows = data?.markers || [];
    const q = query.trim().toLowerCase();
    return rows.filter((m) => {
      if (group !== "всички" && m.group_bg !== group) return false;
      if (!q) return true;
      return (m.label_bg + " " + m.owner_label_bg + " " + m.key).toLowerCase().includes(q);
    });
  }, [data, query, group]);

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}
      data-testid="marker-map-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs font-bold opacity-70 hover:opacity-100"
          data-testid="marker-map-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </Link>

        <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold">Карта на показателите</h1>
        <p className="text-sm opacity-70 mt-1 max-w-3xl">
          {data?.intro_bg
            || "Всеки показател се оценява от един близнак — неговия собственик."}
        </p>
        {data && (
          <p className="text-xs opacity-60 mt-1" data-testid="marker-map-count">
            {data.with_value} от {data.total} показателя имат Ваша стойност
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${cardCls}`}>
            <Search className="w-3.5 h-3.5 opacity-50" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Търсене на показател"
              data-testid="marker-map-search"
              className="bg-transparent outline-none text-sm w-48"
            />
          </div>
          {["всички", ...(data?.groups || [])].map((g) => (
            <button key={g} type="button" onClick={() => setGroup(g)}
              data-testid={`marker-map-group-${g}`}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                group === g
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : isDark ? "border-slate-700" : "border-slate-200"}`}>
              {g}
            </button>
          ))}
        </div>

        {!data && (
          <div className="mt-8 flex items-center gap-2 text-sm opacity-70">
            <Loader2 className="w-4 h-4 animate-spin" /> Зареждам картата…
          </div>
        )}

        <div className="mt-4 space-y-2">
          {shown.map((m) => (
            <div key={m.key} className={`rounded-2xl border p-3.5 ${cardCls}`}
              data-testid={`marker-row-${m.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-bold">{m.label_bg}</div>
                  <div className="text-[11px] opacity-60 mt-0.5">
                    Ориентир: {m.reference_bg}{m.unit ? ` ${m.unit}` : ""} · {m.group_bg}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {m.value != null ? (
                    <div className="text-lg font-bold" data-testid={`marker-value-${m.key}`}>
                      {typeof m.value === "number" ? Number(m.value.toFixed(2)) : m.value}
                      {m.unit ? <span className="text-[11px] opacity-60 ml-1">{m.unit}</span> : null}
                    </div>
                  ) : (
                    <div className="text-[11px] opacity-50" data-testid={`marker-value-${m.key}`}>
                      няма въведена стойност
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                <button type="button" onClick={() => m.owner_route && navigate(m.owner_route)}
                  data-testid={`marker-owner-${m.key}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-bold ${
                    isDark ? "bg-indigo-900/40 text-indigo-200" : "bg-indigo-50 text-indigo-700"}`}>
                  оценява се от: {m.owner_label_bg} <ArrowRight className="w-3 h-3" />
                </button>
                {m.used_by.length > 0 && (
                  <span className="opacity-70">
                    ползва се и от: {m.used_by.map((u) => u.label_bg).join(", ")}
                  </span>
                )}
              </div>
            </div>
          ))}
          {data && shown.length === 0 && (
            <p className="text-sm opacity-60 mt-4">Няма показател по това търсене.</p>
          )}
        </div>
      </div>
    </div>
  );
}
